import { Mail } from '@/src/assets/icons/Mail'
import { BackButton } from '@/src/common/components/BackButton'
import { Input, InputPhone } from '@/src/common/components/Input'
import { Routes } from '@/src/common/enums/routes'
import { useBasicInfoCardApplicationStore } from '@/src/common/store/useBasicInfoCardApplicationStore'
import {
  type PiiInformationStore,
  usePiiInformationStore,
} from '@/src/common/store/usePiiInformationStore'
import { useUserStore } from '@/src/common/store/useUserStore'
import { InputSsn } from '@/src/common/components/Input/InputSsn'
import { useState, useEffect } from 'react'
import { Platform, StyleSheet, Text } from 'react-native'
import { ScrollView, View } from 'react-native'
import { formatCurrency } from '@/src/common/utils/general'
import { parsePhoneNumber } from 'libphonenumber-js'
import { useRouter } from 'expo-router'
import { Button } from '@/src/common/components/Button'
import { BirthdateInput } from '@/src/common/components/Input/InputBirthdate'
import { useCardApplicationReferralCodeStore } from '@/src/common/store/useCardApplicationReferralCodeStore'
import { useSardine } from '@/src/services/Sardine'
import { CardApplicationWrapper } from './cardApplicationWrapper'
import { mesaColorNames, typography } from '@/src/common/theme'
import * as E2E from '../../e2e'

interface IApplicationInput extends PiiInformationStore {
  firstName: string
  lastName: string
  phoneNumber: string
  email: string
  referralCode?: string
}

export function ApplicationConfirmation() {
  const router = useRouter()
  const { updateOptions } = useSardine()
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  const { userInformation, countryCode } = useUserStore()
  const { basicInformation } = useBasicInfoCardApplicationStore()
  const { userPiiInformation } = usePiiInformationStore()
  const { referralCode } = useCardApplicationReferralCodeStore()

  const phoneNumberInDatabase = userInformation?.phoneNumber

  const phoneNumberWithoutCountryCode =
    phoneNumberInDatabase != null
      ? parsePhoneNumber(phoneNumberInDatabase, {
          defaultCountry: 'US',
        }).formatNational()
      : phoneNumberInDatabase

  const [applicationInput, setApplicationInput] = useState<IApplicationInput>({
    firstName: basicInformation?.firstName ?? '',
    lastName: basicInformation?.lastName ?? '',
    phoneNumber: phoneNumberWithoutCountryCode ?? '',
    email: userInformation?.email ?? '',
    dob: new Date(userPiiInformation?.dob ?? ''),
    ssn: userPiiInformation?.ssn ?? '',
    totalAnnualIncome: userPiiInformation?.totalAnnualIncome ?? '',
    monthlyHousingPayment: userPiiInformation?.monthlyHousingPayment ?? '',
    referralCode: referralCode?.trim(),
  })

  useEffect(() => {
    void updateOptions({ flow: 'application-confirmation' })
  }, [updateOptions])

  const handleSubmit = () => {
    router.push(Routes.ApplicationCardHolderAgreement.route)
  }

  return (
    <CardApplicationWrapper>
      <ScrollView
        showsVerticalScrollIndicator={false}
        style={Platform.OS === 'web' && { overflow: 'visible' }}
        contentContainerStyle={{ gap: 10 }}
      >
        <BackButton
          href="/main/card-application/lastQuestion"
          text="Back"
          testID={E2E.Config.Ids.Press.ReviewBack}
        />
        <View style={styles.headerText}>
          <Text style={typography['heading/heading2']}>Make sure everything looks right</Text>
        </View>
        <Text style={[typography['paragraph/md'], styles.instructionText]}>
          Review your information below and make any edits as needed prior to submission
        </Text>

        <Input
          label="First name"
          readOnly
          disabled
          defaultValue={applicationInput.firstName}
          textContentType="name"
          autoCorrect={false}
          segmentAction="User Inputs First Name"
          placeholder="First Name"
          onChangeText={(text) => setApplicationInput({ ...applicationInput, firstName: text })}
          testID={E2E.Config.Ids.Input.FirstName}
        />

        <Input
          label="Last name"
          readOnly
          disabled
          defaultValue={applicationInput.lastName}
          textContentType="name"
          autoCorrect={false}
          segmentAction="User Inputs Last Name"
          placeholder="Last Name"
          onChangeText={(text) => setApplicationInput({ ...applicationInput, lastName: text })}
          testID={E2E.Config.Ids.Input.LastName}
        />

        <InputPhone
          label="Phone number"
          readOnly
          disabled
          defaultValue={phoneNumberWithoutCountryCode ?? undefined}
          placeholder="Phone number"
          inputValue={{ phoneNumber: phoneNumberWithoutCountryCode ?? '', countryCode }}
          keyboardType="phone-pad"
          testID={E2E.Config.Ids.Input.PhoneNumber}
        />

        <Input
          label="Email address"
          readOnly
          disabled
          defaultValue={applicationInput.email}
          textContentType="emailAddress"
          leftIcon={<Mail stroke={mesaColorNames['Greyscale/400 #A4AAAC']} />}
          segmentAction="User Inputs Email"
          placeholder="Email Address"
          value={userInformation?.email}
          testID={E2E.Config.Ids.Input.Email}
        />

        <BirthdateInput
          label="Date of birth"
          value={applicationInput.dob}
          disabled={true}
          testID={E2E.Config.Ids.Input.DateOfBirth}
        />

        <InputSsn
          label="Social security number"
          readOnly
          disabled
          value={applicationInput.ssn}
          onChange={(ssn) => setApplicationInput({ ...applicationInput, ssn })}
          placeholder="Social security number"
          testID={E2E.Config.Ids.Input.SSN}
        />

        <Input
          label="Total annual income"
          readOnly
          disabled
          placeholder="Total Annual Income"
          value={formatCurrency(applicationInput.totalAnnualIncome, {
            decimals: 0,
            minimumFractionDigits: 0,
          })}
          keyboardType="numeric"
          testID={E2E.Config.Ids.Input.TotalAnnualIncome}
        />

        <Input
          label="Monthly housing payment"
          readOnly
          disabled
          placeholder="Monthly Housing Payment"
          value={formatCurrency(applicationInput.monthlyHousingPayment, {
            decimals: 0,
            minimumFractionDigits: 0,
          })}
          keyboardType="numeric"
          testID={E2E.Config.Ids.Input.MonthlyHousingPayment}
        />

        {referralCode && (
          <Input
            label="Referral code"
            readOnly
            disabled
            placeholder="Referral code"
            value={applicationInput.referralCode}
            testID={E2E.Config.Ids.Input.OfferCode}
          />
        )}
      </ScrollView>
      <View style={{ marginTop: 32, marginBottom: 10 }}>
        <Button
          title="Continue"
          onPress={handleSubmit}
          testID={E2E.Config.Ids.Press.ReviewContinue}
        />
      </View>
    </CardApplicationWrapper>
  )
}

const styles = StyleSheet.create({
  headerText: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 32,
  },
  instructionText: {
    marginVertical: 16,
    lineHeight: 24,
    color: mesaColorNames['Greyscale/400 #A4AAAC'],
  },
} as const)
